import { request } from '@umijs/max';

export interface AppConfigRecord {
  id?: number;
  config_key: string;
  config_value: string;
  config_type?: 'string' | 'number' | 'boolean' | 'json';
  description?: string | null;
  created_at?: string;
  updated_at?: string;
}

const STORAGE_KEY = 'task_management_app_config_v1';

const canUseStorage =
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readConfigMap = (): Record<string, AppConfigRecord> => {
  if (!canUseStorage) return {};
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return {};
  try {
    return JSON.parse(raw) as Record<string, AppConfigRecord>;
  } catch {
    return {};
  }
};

const writeConfigMap = (map: Record<string, AppConfigRecord>) => {
  if (!canUseStorage) return;
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
};

const getFallbackFromHttp = async (): Promise<AppConfigRecord[]> => {
  try {
    const data = await request<{ configs?: AppConfigRecord[] }>(
      '/bff/app-configs',
      {
        method: 'GET',
        skipErrorHandler: true,
      },
    );
    return data?.configs || [];
  } catch {
    return [];
  }
};

export const appConfigApi = {
  getList: async (): Promise<AppConfigRecord[]> => {
    const localMap = readConfigMap();
    const localList = Object.values(localMap);
    if (localList.length > 0) return localList;
    return getFallbackFromHttp();
  },

  getByKey: async (key: string): Promise<AppConfigRecord | null> => {
    const localMap = readConfigMap();
    if (localMap[key]) return localMap[key];

    try {
      const listData = await request<{ configs?: AppConfigRecord[] }>(
        '/bff/app-configs',
        {
          method: 'GET',
          skipErrorHandler: true,
        },
      );
      const configs = listData?.configs || [];
      const record = configs.find((item) => item.config_key === key) || null;
      if (record) {
        localMap[key] = record;
        writeConfigMap(localMap);
      }
      return record;
    } catch {
      return null;
    }
  },

  upsertByKey: async (
    key: string,
    value: string,
    type: 'string' | 'number' | 'boolean' | 'json' = 'string',
  ): Promise<AppConfigRecord> => {
    const localMap = readConfigMap();
    const now = new Date().toISOString();
    const record: AppConfigRecord = {
      ...(localMap[key] || {}),
      config_key: key,
      config_value: value,
      config_type: type,
      updated_at: now,
      created_at: localMap[key]?.created_at || now,
    };
    localMap[key] = record;
    writeConfigMap(localMap);

    request('/bff/app-configs/upsert-by-key', {
      method: 'POST',
      data: { key, value, type },
      skipErrorHandler: true,
    }).catch(() => undefined);

    return record;
  },
};
